/**
 * Location diffing utilities
 *
 * Optimizes React re-renders by detecting and updating only units that moved
 */

import type { Unit } from '@/types';

export interface LocationChange {
  type: 'moved' | 'new';
  unitId: string;
  oldLocation?: { lat: number; lng: number };
  newLocation?: { lat: number; lng: number };
}

/**
 * Compare two locations to detect changes
 * Uses a small threshold to avoid updates due to floating point precision
 */
function locationsEqual(
  loc1: { lat: number; lng: number },
  loc2: { lat: number; lng: number },
  threshold = 0.0001 // ~11 meters at equator
): boolean {
  return (
    Math.abs(loc1.lat - loc2.lat) < threshold &&
    Math.abs(loc1.lng - loc2.lng) < threshold
  );
}

/**
 * Detect location changes between current and new unit data
 * Returns only units that have moved or are new
 * NOTE: Does NOT detect removed units - partial updates should not remove units
 */
export function detectLocationChanges(
  currentUnits: Unit[],
  newLocations: Array<{ id: string; lat: number; lng: number }>
): LocationChange[] {
  const changes: LocationChange[] = [];
  const currentMap = new Map(currentUnits.map(t => [t.id, t]));

  for (const newLoc of newLocations) {
    const current = currentMap.get(newLoc.id);

    if (!current) {
      // New unit
      changes.push({
        type: 'new',
        unitId: newLoc.id,
        newLocation: { lat: newLoc.lat, lng: newLoc.lng }
      });
    } else {
      // Check if location changed
      const oldLocation = current.location;
      const newLocation = { lat: newLoc.lat, lng: newLoc.lng };

      if (!locationsEqual(oldLocation, newLocation)) {
        changes.push({
          type: 'moved',
          unitId: newLoc.id,
          oldLocation,
          newLocation
        });
      }
    }
  }

  return changes;
}

/**
 * Update unit array with location changes
 * Preserves object references for unchanged units (React optimization)
 */
export function applyLocationChanges(
  currentUnits: Unit[],
  changes: LocationChange[]
): Unit[] {
  if (changes.length === 0) {
    return currentUnits; // No changes, preserve exact reference
  }

  const changedIds = new Set(changes.map(c => c.unitId));

  // Only map over existing units - we don't filter out "removed" ones anymore
  return currentUnits.map(unit => {
      if (!changedIds.has(unit.id)) {
        return unit; // Preserve reference for unchanged unit
      }

      const change = changes.find(c => c.unitId === unit.id);
      if (!change) return unit;

      // Create new unit object with updated location
      if (!change.newLocation) return unit;

      return {
        ...unit,
        location: {
          ...unit.location,
          lat: change.newLocation.lat,
          lng: change.newLocation.lng
        }
      };
    });
}

/**
 * Helper to update units with new location data
 * Combines detection and application in one function
 */
export function updateUnitLocations(
  currentUnits: Unit[],
  newLocations: Array<{ id: string; lat: number; lng: number }>
): { units: Unit[]; changedCount: number } {
  const changes = detectLocationChanges(currentUnits, newLocations);
  const units = applyLocationChanges(currentUnits, changes);

  return {
    units,
    changedCount: changes.length
  };
}
